import type { ILeadDetail } from '@user/store/lead'
import { Theme, useTheme } from '@mui/material/styles'
import type { IOpportunityDetail } from '@user/store/opportunity'
import { ENTITY_TYPES } from '@sales-center/common'
import { LEAD_STATUSES } from '@user/components/Leads/LeadCard/typings'
import { OPPORTUNITY_STATUSES } from '@user/components/Opportunities/OpportunityCard/typings'
import { useUpdateReferral } from '@user/components/DetailPages/sections/AdditionalInformation/hooks/useUpdateReferral'
import { useGetCustomFieldsOnMount } from '@user/store/customFields'
import { useGetCustomFieldsSettingsOnMount } from '@user/store/customFieldSettings'
import { useGetEntityFormSettingsOnMount } from '@user/store/entityFormSettings'
import CircularProgress from '@mui/material/CircularProgress'
import { InfoTooltip, TabPanel } from '@sales-center/components'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { EditButton } from '@user/components/DetailPages/sections/AdditionalInformation/Edit/EditButton'
import Grid from '@mui/material/Grid'
import { CustomField } from '@user/components/CustomFields'
import * as React from 'react'
import { useMemo } from 'react'
import {
  EntityDetailsUpdateReferral,
  TabsStyled,
  TabStyled,
} from '@user/features/EntityDetails/components'
import { useData, useHandlers } from './hooks'
import {
  entityReferral,
  useGetEntityFormDataOnMount,
} from '@user/components/Settings/EntityFormSettings'
import { CallTrackingSection } from '@user/features/EntityDetails/components/CallTrackingSection'
import { useCampaignTrackingEnabled } from '@user/features/CampaignTracking'
import { useGetEmployeeListOnMount } from '@user/store/employee/hooks/useGetEmployeeListOnMount'
import { useGetUserListOnMount } from '@user/store/users'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'
import { useSelector } from 'react-redux'
import { selectAllSources, useGetSourcesOnMount } from '@user/store/sources'
import { WebFormButton } from '@user/features/CampaignTracking/WebForm'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { EntityRecordTypes } from '@user/features/EntityDetails/components/EntityDetailsAdditionalInformation/typings'
import { DraftLeadTypeTooltipContents } from '@user/components/DraftLeads/DraftLeadTypeTooltip/DraftLeadTypeTooltipContents'

const styles = {
  tooltip: {
    fontSize: '14px',
    marginLeft: (theme: Theme) => theme.spacing(0.5),
    marginBottom: (theme: Theme) => theme.spacing(0.2),
  },
}

interface Props {
  detail: ILeadDetail | IOpportunityDetail
  entityId: number
  entityType: ENTITY_TYPES
  leadStatus?: LEAD_STATUSES
  opportunityStatus?: OPPORTUNITY_STATUSES
  canUpdate: boolean
  salesTeamId: string
  entityMatched: boolean
  leadCreatedTime?: Date
}

export function EntityDetailsAdditionalInformation(props: Props) {
  const {
    detail,
    entityId,
    entityType,
    canUpdate,
    salesTeamId,
    entityMatched,
    leadCreatedTime,
  } = props

  const {
    customFieldValues,
    targetPestList,
    showEditButton,
    ownerTitle,
    customFields,
    customFieldOptions,
  } = useData(props)

  const campaignTrackingEnabled = useCampaignTrackingEnabled()
  const sourcesStatus = useGetSourcesOnMount()
  const { status, execute } = useUpdateReferral()
  const customFieldsLoading = useGetCustomFieldsOnMount()
  const { loading: customSettingsLoading } =
    useGetCustomFieldsSettingsOnMount(entityType)
  const { loading: entityFormSettingsLoading } =
    useGetEntityFormSettingsOnMount(entityType)

  const customDataLoading = customFieldsLoading && customSettingsLoading

  const {
    handleReferralUpdate,
    setColor,
    handleChange,
    tabIndex,
    toggleShowEmpty,
    showEmpty,
  } = useHandlers({
    ...props,
    execute,
  })

  const { entitySettingsMap } = useGetEntityFormDataOnMount(entityType)
  const [requiredReferral] = entitySettingsMap.get(entityReferral) ?? [
    false,
    true,
  ]

  const employeeListStatus = useGetEmployeeListOnMount()
  const userListStatus = useGetUserListOnMount()
  const { formatDate } = useLocalizationDateFormat()

  const leadSources = useSelector(selectAllSources)
  const leadSourceValue = useMemo(() => {
    if (!detail.leadSource) {
      const leadSource = leadSources.find((x) => x.id == detail.leadSourceId)
      return leadSource ? leadSource.name : ''
    } else {
      return detail.leadSource
    }
  }, [detail.leadSource, detail.leadSourceId, leadSources])

  const filteredCustomFieldValues = useMemo(() => {
    return showEmpty
      ? customFieldValues
      : customFieldValues.filter(
          (x) =>
            x.customFieldOptionId ||
            (x.customFieldValueOptions &&
              x.customFieldValueOptions.length > 0) ||
            x.date ||
            x.number ||
            x.text ||
            x.bool !== null,
        )
  }, [customFieldValues, showEmpty])

  const typeWeb = detail?.type?.toLowerCase() === EntityRecordTypes.web
  const typeImport = detail?.type?.toLowerCase() === EntityRecordTypes.import
  const theme = useTheme()

  return entityFormSettingsLoading && sourcesStatus == 'pending' ? (
    <CircularProgress />
  ) : (
    <>
      <TabsStyled
        indicatorColor="primary"
        onChange={handleChange}
        textColor="primary"
        value={tabIndex}
      >
        <TabStyled
          label="Details"
          color={setColor(0)}
          data-test-id="detailsTab"
        />
        <TabStyled
          label="Referral"
          color={setColor(1)}
          data-test-id="referralTab"
        />
        {campaignTrackingEnabled && (
          <TabStyled
            label="Call Tracking"
            color={setColor(2)}
            data-test-id="callTrackingTab"
          />
        )}
      </TabsStyled>

      <TabPanel value={0} tab={tabIndex}>
        <Stack p={2} pb={1.2}>
          <Stack direction="row" spacing={2} alignItems="center" mb={3}>
            <Typography variant="subtitle1" color="#000000">
              Details
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              {showEditButton && (
                <EditButton
                  entityId={entityId}
                  entityType={entityType}
                  dataTestId="editAdditionalInfoBtn"
                />
              )}
            </Box>
          </Stack>
          <Grid
            container
            direction="row"
            spacing={3}
            // WWM-18181: top-aligned, not centred. A Text custom field can now
            // wrap to several lines, and centring re-centres that whole grid
            // item, knocking every sibling field's label out of line. Matches
            // DetailPages/sections/AdditionalInformation, which sets no
            // alignItems at all.
            alignItems="flex-start"
            mb={3}
          >
            {(showEmpty || !!detail.submittedBy) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Submitted By
                  <InfoTooltip
                    sx={styles.tooltip}
                    infoText="This is who initially submitted this record as a Pending Lead"
                  />
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.submittedBy ? detail.submittedBy : '-'}
                </Typography>
              </Grid>
            )}
            <Grid item>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Owner
              </Typography>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.primary}
              >
                {ownerTitle}
              </Typography>
            </Grid>

            {(showEmpty || !!detail.campaign) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Campaign Name
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.campaign || <>-</>}
                </Typography>
              </Grid>
            )}

            {(showEmpty || !!detail.type) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                  alignItems="center"
                  display="flex"
                  gap="5px"
                >
                  Type{' '}
                  <InfoTooltip infoText={<DraftLeadTypeTooltipContents />} />
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.type}
                </Typography>
              </Grid>
            )}

            {entityType == ENTITY_TYPES.Opportunity ? (
              <>
                {(showEmpty ||
                  (detail as IOpportunityDetail)?.leadCreatedBy) && (
                  <Grid item>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}
                    >
                      Lead Created By
                      <InfoTooltip
                        sx={styles.tooltip}
                        infoText={
                          'This is the user who added the lead into the system. This will be blank if the record was initially created as an opportunity.'
                        }
                      />
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color={theme.palette.text.primary}
                    >
                      {(detail as IOpportunityDetail)?.leadCreatedBy
                        ?.fullName || <>-</>}
                    </Typography>
                  </Grid>
                )}
              </>
            ) : null}

            {(showEmpty || detail.createdBy) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  {entityType == ENTITY_TYPES.Lead
                    ? 'Lead Created By'
                    : 'Opportunity Created By'}
                  <InfoTooltip
                    sx={styles.tooltip}
                    infoText={
                      entityType == ENTITY_TYPES.Lead
                        ? 'This is the user who added the lead into the system.'
                        : 'This is the user who added the opportunity into the system or the user who converted the lead into an opportunity.'
                    }
                  />
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.createdBy.fullName || <>-</>}
                </Typography>
              </Grid>
            )}

            {entityType == ENTITY_TYPES.Lead &&
              (showEmpty || !!detail.createdTime) && (
                <Grid item>
                  <Typography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    Lead Created Date
                    <InfoTooltip
                      sx={styles.tooltip}
                      infoText="This is the date the lead was added into the system. This will be blank if the record was initially created as an opportunity."
                    />
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color={theme.palette.text.primary}
                  >
                    {formatDate(detail.createdTime) || <>-</>}
                  </Typography>
                </Grid>
              )}
            {entityType == ENTITY_TYPES.Opportunity && (
              <>
                {(showEmpty || !!leadCreatedTime) && (
                  <Grid item>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}
                    >
                      Lead Created Date
                      <InfoTooltip
                        sx={styles.tooltip}
                        infoText="This is the date the lead was added into the system. This will be blank if the record was initially created as an opportunity."
                      />
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color={theme.palette.text.primary}
                    >
                      {formatDate(leadCreatedTime) || <>-</>}
                    </Typography>
                  </Grid>
                )}
                {(showEmpty || !!detail.createdTime) && (
                  <Grid item>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.secondary}
                    >
                      Opportunity Created Date
                      <InfoTooltip
                        sx={styles.tooltip}
                        infoText="This is the date the opportunity was added into the system or the date the lead was converted to an opportunity"
                      />
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color={theme.palette.text.primary}
                    >
                      {formatDate(detail.createdTime) || <>-</>}
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {(showEmpty || !!detail.salesFunnel) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Sales Funnel
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.salesFunnel || <>-</>}
                </Typography>
              </Grid>
            )}

            {(showEmpty || !!detail.salesTeam) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Sales Team
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {detail.salesTeam || <>-</>}
                </Typography>
              </Grid>
            )}

            <Grid item>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Age
                <InfoTooltip
                  sx={styles.tooltip}
                  infoText="Age represents the time a record has been active. Aging begins when records are first created in the system, stops when closed, and resumes when re-opened"
                />
              </Typography>
              <Typography
                variant="subtitle2"
                color={theme.palette.text.primary}
              >
                {detail.age ? (
                  `${detail.age} day${detail.age === 1 ? '' : 's'}`
                ) : (
                  <>0</>
                )}
              </Typography>
            </Grid>

            {(showEmpty || !!leadSourceValue) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Lead Source
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {leadSourceValue ? leadSourceValue : <>-</>}
                </Typography>
              </Grid>
            )}

            {(showEmpty || !!targetPestList) && (
              <Grid item>
                <Typography
                  variant="body2"
                  color={theme.palette.text.secondary}
                >
                  Target Pests
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.primary}
                >
                  {targetPestList ? targetPestList : <>-</>}
                </Typography>
              </Grid>
            )}

            {customDataLoading ? (
              <Grid item xs={6} sm={4} md={3} lg={2}>
                <CircularProgress size={25} />
              </Grid>
            ) : (
              <>
                {filteredCustomFieldValues.map((f) => (
                  <CustomField
                    key={f.id}
                    value={f}
                    customFields={customFields}
                    customFieldOptions={customFieldOptions}
                  />
                ))}
              </>
            )}

            {((detail.callTrackingEventId &&
              detail.type == EntityRecordTypes.web) ||
              detail.pendingLeadId) &&
              !typeImport && (
                <Grid item>
                  <Typography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {typeWeb ? 'Webform Submission' : 'Captured Information'}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color={theme.palette.text.primary}
                  >
                    <WebFormButton
                      hideInfoBox
                      webFormCallTrackingEventId={detail.callTrackingEventId}
                      type={detail.type}
                      draftLeadId={detail.pendingLeadId}
                      createdTime={leadCreatedTime}
                      noIcon
                      variant="link"
                      text={
                        typeWeb
                          ? 'View Submitted Webform'
                          : 'View Captured Information'
                      }
                    />
                  </Typography>
                </Grid>
              )}
          </Grid>

          <Box width="fit-content">
            <FormControlLabel
              control={
                <Switch
                  checked={showEmpty}
                  onChange={toggleShowEmpty}
                  size="small"
                />
              }
              label="Show empty fields"
              color={theme.palette.text.primary}
              data-test-id="showEmptyFieldsSwitch"
            />
          </Box>
        </Stack>
      </TabPanel>

      <TabPanel value={1} tab={tabIndex}>
        {status === 'pending' ? (
          <CircularProgress />
        ) : (
          <EntityDetailsUpdateReferral
            billToCardMaxWidth="400px"
            referral={
              detail.referralSummary && detail.referralSummary.referralType
                ? detail.referralSummary
                : undefined
            }
            onChange={handleReferralUpdate}
            padding="12px 0"
            salesTeamId={salesTeamId}
            detail={detail}
            canUpdate={canUpdate}
            leadMatched={entityMatched}
            required={requiredReferral}
            employeeListStatus={employeeListStatus}
            userListStatus={userListStatus}
          />
        )}
      </TabPanel>

      <TabPanel value={2} tab={tabIndex}>
        <CallTrackingSection type={entityType} id={entityId} />
      </TabPanel>
    </>
  )
}
